<script>
	/**
	 * This top-level route allows a teacher user to search for articles to either
	 * add to their binder (aka "My List") or assign to students.
	 *
	 * SUB ROUTES
	 *
	 * None.
	 *
	 * HOW SEARCHES ARE DONE
	 *
	 * In $data of this view, the currentSearch object has properties that correspond
	 * to the parameters to discoverySearch (endpoint /v1/d/s).
	 *
	 * This route will also put each of the properties of currentSearch
	 * into the hash store. This has the advantage that the user can use the
	 * browser back and forward buttons to navigate through search criteria.
	 * It also allows us to easily see from the address bar what search is
	 * going to be performed.
	 *
	 * We also display pill buttons for each search criterion. Clicking the pill
	 * will undo (or remove) that search criterion.
	 *
	 * When this route first loads, we initialize the currentSearch object
	 * from the hash. At this stage there are no watchers on currentSearch
	 * (since it would probably start an endless loop). Once currentSearch is
	 * initialized, we trigger a search that emits the data-ready event and then
	 * all of the watchers on currentSearch are initialized using a $once handler
	 * (that is we only call the handler once, since every completed search emits
	 * data-ready).
	 *
	 * Every time a property of currentSearch changes a watcher fires.
	 * onCurrentSearchChange(), turns the
	 * currentSearch object into a new hash, recreates the list of undo
	 * pills based on the search and then triggers the actual search.
	 *
	 * Put another way, any new search criteria or changes will require
	 * alterations in several methods:
	 * - hash entry in keys.js
	 * - the new search field name and initial value must be entered in BLANK_SEARCH
	 *
	 */

	var _ = require('underscore');
	var discoveryService = require('services/discoveryService');
	var utilities = require('utilities');
	var scrollManager = require('ui/core/scrollManager');
	var SEARCH_UNAVAILABLE = 1056;
	var DebouncedQueue = require('async/debouncedQueue');
	var Vue = require('vue');
	var Q = require('q');
	var crudService = require('services/crudService');
	var model = require('model/model');
	const page = require('page');
	const productService = require('services/productService');
	const breakpoints = require('utilities/breakpoints');
	const classUtilities = require('utilities/classUtilities');
	const metadata = require('core/metadata');
	const { startJourney, JOURNEYS, MILESTONES, updateMilestone } = require('utilities/journeyUtilities');

	const PARENT_WHITELIST = metadata.PARENT_WHITELIST;

	const ARTICLE_ID = 'A';
	const PAIRED_TEXT_ID = 'P';
	const AAD_ID = 'AAD';
	const TS_ID = 'TS';

	const analyticsUtilities = require('utilities/analyticsUtilities');

	const fireEvent = analyticsUtilities.fireEvent;
	const SEARCH_SUGGESTION_CLICK = analyticsUtilities.events.SEARCH_SUGGESTION_CLICK;
	const PRODUCT_PROMOTION_CLICK = analyticsUtilities.events.PRODUCT_PROMOTION_CLICK;
	const ADD_TO_BINDER = analyticsUtilities.events.ADD_TO_BINDER;
	const REMOVE_FROM_BINDER = analyticsUtilities.events.REMOVE_FROM_BINDER;

	const { assetUrl } = require('utilities/urlUtilities');

	const SEARCH = 'search';
	const SCOPE_AND_SEQUENCE = 'scope-and-sequence';
	const RMS = 'rms';
	const searchTabs = {
		[SEARCH]: {
			key: SEARCH,
			default: true
		},
		[SCOPE_AND_SEQUENCE]: {
			key: SCOPE_AND_SEQUENCE,
			auth: true
		},
		[RMS]: {
			key: RMS,
			auth: true
		}
	};

	const config = require('core/config');
	let debug = false;
	let host = config.host;

	// Do not allow debug on prod
	if (host !== 'www.readworks.org') {
		debug = window.location.search && window.location.search.indexOf('debugSearch') !== -1;
	}

	var findArticlesQueue = new DebouncedQueue({
		threshhold: 1000
	});

	var EMPTY_RESULTS = {
		hits: [],
		count: 0,
		total: 0
	};
	var BLANK_SEARCH = {
		s0: [],
		q: '', // the query string, String
		g: [], // grade level IDs
		t: [], // text type
		s: [], // the "subjects", tags that are children of "Domain"
		k: [], // tags that are children of "Skills & Strategies"
		cid: [], // tags that are children of "Special Collections"
		f: 0, // page
		lc: [], // lexile codes, e.g. NP, BR
		l: [], // lexile ranges,
		pt: [],
		sr: false, // step reads
		features: [], // array of product features, just 'au' (audio) for now
		staff_picks: [],
		sel: [],
		at: [],
		subType: [],
		book: 0,
		wc: [],
		series: []
	};

	// Each parameter for search needs to be in 3 places:
	// 1 - hash entry in keys.js
	// 2 - A sane default in BLANK_SEARCH

	module.exports = {
		props: ['on-ready', 'route-context', 'title-override'],
		components: {
			'article-image': require('components/article-image.vue').default,
			'article-search-result': require('components/article-search-result.vue').default,
			'undo-pills': require('components/undo-pills.vue').default,
			'grade-selector': require('components/grade-selector.vue').default,
			'product-selector': require('components/product-selector.vue').default,
			'topic-selector': require('components/topic-selector.vue').default,
			'range-slider': require('components/range-slider.vue').default,
			pagination: require('components/pagination.vue').default,
			'input-item-with-label': require('components/input-item-with-label.vue').default,
			'search-bar-with-suggestions': require('components/search-bar-with-suggestions.vue').default,
			'butter-bar-promo': require('components/butter-bar-promo.vue').default,
			'featured-content': require('components/product-promotion/featured-content.vue').default,
			'featured-topics': require('components/product-promotion/featured-topics.vue').default,
			'scope-and-sequence-promotion-panel': require('components/product-promotion/scope-and-sequence-promotion-panel.vue').default,
			'article-promotion-panel': require('components/product-promotion/article-promotion-panel.vue').default,
			'paired-text-promotion-panel': require('components/product-promotion/paired-text-promotion-panel.vue').default,
			'aad-promotion-panel': require('components/product-promotion/aad-promotion-panel.vue').default,
			'recommended-content-panel': require('components/product-promotion/recommended-content-panel.vue').default,
			'promotional-collection-panel': require('components/promotional-collection-panel.vue').default,
			'product-selection-hidden-radio-group': require('components/product-selection-hidden-radio-group.vue').default,
			'featured-content-carousel': require('components/featured-content-carousel.vue').default,
			'magnifying-glass-illustration': require('components/svg/magnifying-glass-illustration.vue').default,
			'tab-container': require('components/tab-container.vue').default,
			'scope-and-sequence': require('components/scope-and-sequence.vue').default,
			'student-knowledge-inventory-about-content': require('components/student-knowledge-inventory-about-content.vue').default
		},

		mounted() {
			var self = this;

			if (!this.contentTab) {
				this.contentTab = SEARCH;
			}

			findArticlesQueue.setOptions({
				scope: self,
				onQueueInitialize: () => self.showLoadMask()
			});

			// $data.q drives the input, and on page load should be populated
			if (!self.currentSearch.q) {
				self.currentSearch.q = '';
			}
			self.currentSearch.q = (self.currentSearch.q || '').trim();
			self.q = self.currentSearch.q;

			self.gradeSelections = metadata.gradeSelectionsWithProducts;
			self.textTypeSelections = metadata.textTypes;
			self.textTypeById = metadata.textTypeById;

			var initialLexile = self.currentSearch.l;

			// Check for old Lexile API and convert if possible
			if (initialLexile && (initialLexile.length !== 2 || !initialLexile.every(i => typeof i === 'number'))) {
				let newLexile = [];
				if (initialLexile.length === 1 && Array.isArray(initialLexile[0])) {
					initialLexile = initialLexile[0];
					if (initialLexile.length && initialLexile.every(i => Array.isArray(i))) {
						let firstItem = initialLexile[0];
						let lastItem = initialLexile[initialLexile.length - 1];
						// Old Lexile API
						if (firstItem && firstItem.length === 2 && lastItem && lastItem.length === 2) {
							newLexile.push(firstItem[0]);
							newLexile.push(lastItem[lastItem.length - 1]);
						}
					}
				}

				// Either clear out or converted value
				self.currentSearch.l = newLexile;
			}

			// convert old empty text type links to new empty array
			if (self.currentSearch.t && self.currentSearch.t.length === 1 && self.currentSearch.t[0] === 0) {
				self.currentSearch.t = [];
			}

			//convert old question sets
			if (self.currentSearch.features && self.currentSearch.features.length) {
				if (!self.currentSearch.at) {
					self.currentSearch.at = [];
				}

				let features = self.currentSearch.features;
				let activityTypes = self.currentSearch.at;

				if (features.indexOf('qsmw') !== -1) {
					// qsmw = at:full,text_structure,monitoring,inferring/
					self.addFilterToList(activityTypes, 'full');
					self.addFilterToList(activityTypes, 'text_structure');
					self.addFilterToList(activityTypes, 'inferring');
					self.removeFilterFromList(features, 'qsmw');
				}
				if (features.indexOf('qsmo') !== -1) {
					// qsmo = at:express/
					self.addFilterToList(activityTypes, 'express');
					self.removeFilterFromList(features, 'qsmo');
				}
				if (features.indexOf('qswo') !== -1) {
					// qswo = at:writing/
					self.addFilterToList(activityTypes, 'writing');
					self.removeFilterFromList(features, 'qswo');
				}
			}

			if (self.currentSearch.s0 && self.currentSearch.s0.length) {
				self.setS0(self.currentSearch.s0, true);
			}

			if (this.gcAddon) {
				this.searchExclusions = {
					pt: [TS_ID],
					subType: [7000]
				};
			}

			self.$emit('data-ready');

			self.$watch(
				'currentSearch',
				function (newValue, oldValue) {
					self.findArticles(typeof oldValue === 'undefined');
				},
				{
					deep: true,
					immediate: true
				}
			);

			// Update queryString when store state changes.
			// Can't rely on hashchange events, especially for IE11
			// and our use of pushState/replaceState
			this.$subscribe('hash-store-update', pathData => {
				if (pathData && pathData.h) {
					this.queryString = pathData.h;
				}
			});

			window.addEventListener('scroll', this.onWindowScroll, { passive: true });
		},

		beforeDestroy() {
			window.removeEventListener('scroll', this.onWindowScroll);
		},

		watch: {
			userContext: {
				handler(context) {
					if (context) {
						this.getMyList();
						this.initRecommendations();
					}
				},
				immediate: true
			},
			spotlightPanel: {
				handler(p) {
					if (p && p.products && p.products.length) {
						this.spotlightProducts = null;
						let pids = p.featuredProducts || p.products;
						if (pids.length > 2) {
							pids = pids.slice(0, 2);
						}
						let prms = pids.map(pid => {
							return productService.get(pid);
						});

						Q.all(prms)
							.then(products => {
								this.spotlightProducts = products;
							})
							.done();
					}
				},
				immediate: true
			},
			ellFilterSelected(isSelected) {
				if (isSelected) {
					updateMilestone(MILESTONES.ellFilter);
				}
			}
		},
		computed: {
			/**
			 * some searchable nodes (namely the activity types) are disabled at times
			 * so when they are disabled we don't want to suggest them, so this computed
			 * prop will return searchableNodes with the disabled ones omitted.
			 */
			enabledSearchableNodes() {
				const enabledNodes = metadata.searchableNodes.concat(
					this.productData.map(p => {
						return {
							id: p.id,
							name: p.searchText || p.text,
							parentName: 'Product'
						};
					})
				);

				this.activityTypes.forEach(atype => {
					if (!atype.disabled) {
						enabledNodes.push({
							id: atype.id,
							name: atype.text,
							parentName: 'Activity Type'
						});

						if (atype.children) {
							atype.children.forEach(childAType => {
								// don't add children with same ID as parent, since they're the default
								if (childAType.id !== atype.id) {
									enabledNodes.push({
										id: childAType.id,
										name: childAType.text,
										parentName: atype.text
									});
								}
							});
						}
					}
				});

				return enabledNodes;
			},
			specialCollection: function () {
				if (this.currentSearch && this.currentSearch.s0 && this.currentSearch.s0.length === 2) {
					let ids = this.currentSearch.s0;
					let topic = ids[0];
					let key = ids[1];
					if (topic === metadata.specialCollectionsNode.id) {
						return metadata.allActiveNodesById['_' + key];
					} else {
						return null;
					}
				} else {
					return null;
				}
			},
			activeButterBar() {
				const pt = new Set(this.currentSearch.pt);
				const at = new Set(this.currentSearch.at);

				if (pt.has('AAD')) {
					return 'aad';
				}

				if (at.has('express')) {
					return 'express';
				}

				return '';
			},
			grades: function () {
				let selections = this.gradeSelections;
				return selections.map(g =>
					_.assign({}, g, {
						selected: this.isGradeSelected(g)
					})
				);
			},
			products() {
				let allowedProducts = this.allowedProducts;
				let products = this.productData;

				return products.map(p => {
					let features = undefined;
					let childSelected = false;
					const disabled = !allowedProducts[p.id];
					let subTypes = undefined;
					if (p.features) {
						features = p.features.map(f => {
							let selected = this.isFeatureFiltered(f);
							if (selected) {
								childSelected = true;
							}
							return _.assign({}, f, {
								selected,
								disabled
							});
						});
					}
					if (p.subTypes) {
						subTypes = p.subTypes.map(st => Object.assign({}, st, { selected: this.isSubTypeFiltered(st.id), disabled }));
					}
					return _.assign({}, p, {
						selected: this.isProductFiltered(p) || childSelected,
						features,
						subTypes,
						disabled
					});
				});
			},
			audio() {
				let audioFeatures = this.audioFeatures;
				return audioFeatures.map(f => {
					return _.assign({}, f, {
						selected: this.isFeatureFiltered(f)
					});
				});
			},
			activityTypes() {
				let filteredProducts = this.currentSearch.pt || [];
				let activityTypesData = this.activityTypesData;
				let activityMap = (this.currentSearch.at || []).reduce((acc, a) => {
					acc[a] = true;
					return acc;
				}, {});
				let filteredProductsMap = filteredProducts.reduce((acc, pt) => {
					acc[pt] = true;
					return acc;
				}, {});
				const hasWordCounts = this.currentSearch.wc && this.currentSearch.wc.length === 2;
				const productsWithWordCounts = new Set([ARTICLE_ID, PAIRED_TEXT_ID]);

				return activityTypesData.map(activity => {
					let children = undefined;
					let disabled = false;

					if (filteredProducts.length || hasWordCounts) {
						disabled = !activity.productTypes.some(pt => {
							if (filteredProducts.length) {
								return filteredProductsMap[pt];
							}

							return productsWithWordCounts.has(pt);
						});
					}

					if (activity.children) {
						children = activity.children.map(c => {
							return _.assign({}, c, {
								selected: activityMap[c.id]
							});
						});
					}
					return _.assign({}, activity, {
						selected: activityMap[activity.id],
						children,
						disabled
					});
				});
			},
			allowedProducts() {
				const activityTypes = this.activityTypes;
				const selectedActivities = activityTypes.filter(a => a.selected || (a.children && a.children.some(c => c.selected)));
				const hasWordCounts = this.currentSearch.wc && this.currentSearch.wc.length === 2;
				let productMap = {
					[AAD_ID]: !hasWordCounts,
					[ARTICLE_ID]: true,
					[PAIRED_TEXT_ID]: true,
					[TS_ID]: !hasWordCounts && !this.gcAddon
				};
				if (selectedActivities.length) {
					productMap = selectedActivities.reduce((acc, a) => {
						(a.productTypes || []).forEach(pt => {
							if (productMap[pt] !== false) {
								acc[pt] = true;
							}
						});
						return acc;
					}, {});
				}
				return productMap;
			},
			textTypes: function () {
				return this.textTypeSelections.map(tt => {
					return _.assign({}, tt, {
						selected: this.isTextTypeFiltered(tt)
					});
				});
			},
			lexileChoices: function () {
				return this.lexileChoiceData.map(lc => {
					return _.assign({}, lc, {
						selected: this.isLexileChoiceSelected(lc)
					});
				});
			},
			featuredTopics() {
				let metaTopics = metadata.featuredTopics;
				return metaTopics.map(t => {
					return _.assign({}, t, metadata.allActiveNodesById['_' + t.topic_tag]);
				});
			},
			featuredArticles() {
				let content = null;
				let allContent = this.searchResults.hits;
				if (!this.isFiltered && allContent.length) {
					content = allContent.filter(c => c.productType === ARTICLE_ID);
				}
				return content;
			},
			featuredPairedTexts() {
				let content = null;
				let allContent = this.searchResults.hits;
				if (!this.isFiltered && allContent.length) {
					content = allContent.filter(c => c.productType === PAIRED_TEXT_ID);
				}
				return content;
			},
			featuredAADSets() {
				let content = null;
				let allContent = this.searchResults.hits;
				if (!this.isFiltered && allContent.length) {
					content = allContent.filter(c => c.productType === AAD_ID);
				}
				return content;
			},
			promotionalPanels() {
				let allContent = this.searchResults.hits;
				let featuredArticles = this.featuredArticles;
				let featuredPairedTexts = this.featuredPairedTexts;
				let featuredAADSets = this.featuredAADSets;
				let componentMap = {
					[ARTICLE_ID]: {
						id: ARTICLE_ID,
						componentName: 'article-promotion-panel',
						content: featuredArticles,
						contentTitle: 'Featured Reading Passages',
						featuredContentClass: 'featured-articles'
					},
					[PAIRED_TEXT_ID]: {
						id: PAIRED_TEXT_ID,
						componentName: 'paired-text-promotion-panel',
						content: featuredPairedTexts,
						contentTitle: 'Featured Paired Texts',
						featuredContentClass: 'featured-paired-texts'
					},
					[AAD_ID]: {
						id: AAD_ID,
						componentName: 'aad-promotion-panel',
						content: featuredAADSets,
						contentTitle: 'Featured Article-A-Day Sets',
						featuredContentClass: 'featured-aad-sets'
					}
				};
				const order = new Set();
				let panels = [];

				for (const c of allContent) {
					order.add(c.productType);

					if (order.size === 3) {
						break;
					}
				}

				order.forEach(pt => {
					panels.push(componentMap[pt]);
				});
				return panels;
			},
			spotlightPanel() {
				let spotlightId = metadata.promoId;
				let spotlightNode = null;

				if (spotlightId && metadata.allNodesById['_' + spotlightId]) {
					spotlightNode = metadata.allNodesById['_' + spotlightId];
				}

				return spotlightNode;
			},
			emptyResultsLookupKey() {
				let lookupKey = 'default';
				const selectedActivityTypes = this.activityTypes.reduce((types, item) => {
					if (item.selected || (item.children && item.children.some(child => child.selected))) {
						return types.add(item.groupName);
					}
					return types;
				}, new Set());

				const AADVocabSearch = selectedActivityTypes.has('vocabulary-activity');
				const AADVocabEmptyRange = new Set([22, 323, 343]);
				const AADVocabEmpty = AADVocabSearch && this.isGradeOnlyInSelectedRange(AADVocabEmptyRange);

				const AADVocabHSEmptyRange = new Set([346, 347]);
				const AADVocabHSEmpty = AADVocabSearch && this.isGradeOnlyInSelectedRange(AADVocabHSEmptyRange);

				const AADSetsSearch = this.currentSearch.pt.includes('AAD');
				const AADSetsEmptyRange = new Set([346, 347]);
				const AADSetsEmpty = AADSetsSearch && this.isGradeOnlyInSelectedRange(AADSetsEmptyRange);

				const decodableSearch = /^decodabl(es?)$/.test(this.currentSearch.q);

				/* exclusionary conditions not added as the last truthy value in the array wins in reducer */
				const lookups = [
					{
						condition: AADVocabEmpty,
						key: 'AADVocab'
					},
					{
						condition: AADVocabHSEmpty,
						key: 'AADVocabHS'
					},
					{
						condition: AADSetsEmpty,
						key: 'AADSets'
					},
					{
						condition: decodableSearch,
						key: 'decodables'
					}
				];

				lookupKey = lookups.filter(lookup => lookup.condition).reduce((acc, item) => item.key, lookupKey);

				return lookupKey;
			},
			emptyResultsMessage() {
				return this.emptyResultsMessages[this.emptyResultsLookupKey];
			},
			totalResults() {
				return this.softSearchResults.total || this.searchResults.total;
			},
			currentRecommendedContent() {
				const recommended = this.recommendedContent;
				const classCode = this.recommendationsClassCode;

				return (
					(recommended && recommended[classCode]) || {
						articles: [],
						sortedRecommendations: []
					}
				);
			},
			wordCountsDisabled() {
				const currentSearch = this.currentSearch || {};

				const disabledProductTypes = new Set([TS_ID, AAD_ID]);
				const hasDisabledProductType = (currentSearch.pt || []).some(pt => disabledProductTypes.has(pt));

				const disabledActivities = new Set(['vocabulary', 'meaning_mapper', 'word_detective']);
				const hasDisabledActivity = (currentSearch.at || []).some(at => disabledActivities.has(at));

				return hasDisabledProductType || hasDisabledActivity;
			},
			ellFilterSelected() {
				return this.currentSearch?.features?.includes('ell') || this.isSubTypeFiltered(8001);
			}
		},
		data: function () {
			const pinterestThumbnail = assetUrl('/assets/dist/images/social-media-thumbnail.jpg');
			const loaderImgSrc = assetUrl('/assets/dist/images/loader.gif');

			let activityTypesData = [
				{
					text: 'Full Question Set',
					id: 'full',
					productTypes: [ARTICLE_ID, TS_ID],
					icon: 'info-icon',
					hint:
						'These sets of 8-10 questions provide practice in multiple key reading strategies while scaffolding important information to encourage a thorough understanding of the text.'
				},
				{
					text: 'Express Question Set',
					id: 'express',
					productTypes: [ARTICLE_ID, TS_ID],
					icon: 'info-icon',
					hint:
						'These sets of 5 multiple-choice questions provide practice in a few key reading strategies while scaffolding important information to support an understanding of the main idea of the text.'
				},
				{
					text: 'Open-Ended Question Set',
					id: 'writing',
					productTypes: [ARTICLE_ID, PAIRED_TEXT_ID, TS_ID],
					icon: 'info-icon',
					hint:
						'These question sets support an understanding of the important information in a text through explicit and inferential open-ended questions.'
				},
				{
					groupName: 'inferring-activity',
					parentText: 'Inferring Question Set',
					parentId: 'inferring-parent',
					selectAll: false,
					text: 'Inferring Question Set',
					id: 'inferring',
					productTypes: [ARTICLE_ID, TS_ID],
					icon: 'info-icon',
					hint: 'These questions provide practice in multiple key reading strategies with a focus on the strategy of inferencing.',
					children: [
						{
							text: 'All Inferring',
							id: 'inferring'
						},
						{
							text: 'Author’s Purpose',
							id: '2'
						},
						{
							text: 'Main Idea',
							id: '33'
						},
						{
							text: 'Theme',
							id: '40'
						}
					]
				},
				{
					groupName: 'text-structure-activity',
					parentText: 'Text Structure Question Set',
					parentId: 'text-structure-parent',
					selectAll: false,
					text: 'Text Structure Question Set',
					id: 'text_structure',
					productTypes: [ARTICLE_ID],
					icon: 'info-icon',
					hint: 'These questions provide practice in multiple key reading strategies with a focus on applying knowledge of text structure.',
					children: [
						{
							text: 'All Text Structure',
							id: 'text_structure'
						},
						{
							text: 'Cause & Effect',
							id: '45'
						},
						{
							text: 'Character',
							id: '4'
						},
						{
							text: 'Plot Structure',
							id: '34'
						},
						{
							text: 'Problem/Solution',
							id: '192123'
						},
						{
							text: 'Sequence',
							id: '51'
						},
						{
							text: 'Setting',
							id: '39'
						}
					]
				},
				{
					groupName: 'vocabulary-activity',
					parentText: 'Article-A-Day Vocabulary',
					parentId: 'vocabulary-parent',
					selectAll: false,
					text: 'Article-A-Day Vocabulary',
					id: 'vocabulary',
					productTypes: [AAD_ID],
					icon: 'info-icon',
					hint:
						'Every individual ReadWorks passage has a Vocabulary Widget, which provides in-line definitions and word forms for key Tier 2 and 3 words, and interactive activities that support student exploration of these words. Additional vocabulary activities for Article-A-Day sets are below.',
					children: [
						{
							text: 'All Article-A-Day Vocabulary',
							id: 'vocabulary'
						},
						{
							text: 'Meaning Mapper',
							id: 'meaning_mapper',
							icon: 'info-icon',
							hint:
								'Meaning Mapper is a vocabulary activity for helping 2nd-5th graders build connections among words each day of the Article-A-Day routine.'
						},
						{
							text: 'Word Detective',
							id: 'word_detective',
							icon: 'info-icon',
							hint: 'Word Detective is a vocabulary activity for helping 2nd-5th graders explore word parts on the last day of the Article-A-Day routine.'
						}
					]
				}
			];

			const activityTypeMap = {};
			activityTypesData.forEach(atype => {
				activityTypeMap[`at_${atype.id}`] = {
					activity: atype
				};
				if (atype.children) {
					atype.children.forEach(childAType => {
						// first child sometimes has same ID as parent
						// so prefer the parent in the map it's there
						activityTypeMap[`at_${childAType.id}`] = {
							activity: atype.id === childAType.id ? atype : childAType,
							group: atype.children
						};
					});
				}
			});

			let productData = [
				{
					id: ARTICLE_ID,
					text: 'Reading Passages',
					features: [
						{
							id: 'EBOOK',
							text: 'eBooks',
							icon: 'info-icon',
							hint:
								'These eBooks, based on ReadWorks articles, feature rich illustrations and expressive human-voice audio narration to help engage every student.'
						},
						{
							id: 'S',
							text: 'StepReads™ <span class="font-small">(differentiation)</span>',
							icon: 'info-icon',
							hint:
								'StepReads™ are less complex versions of the original article. StepRead1 (SR1) is less complex than the original article, and StepRead2 (SR2) is less complex than SR1.  <a href="/teacher-guide/differentiation-content.html#stepreads"> Learn more&#8230;</a>'
						}
					]
				},
				{
					id: AAD_ID,
					text: 'Article-A-Day<sup><small>TM</small></sup> Sets',
					icon: 'info-icon',
					hint:
						'Article-A-Day<sup><small>TM</small></sup> is a 10-minute daily routine that dramatically increases your students\' background knowledge, vocabulary, and stamina. <a href="/teacher-guide/article-a-day-routine.html">See how...</a>',
					alt: 'Article-A-Day',
					searchText: 'Article-A-Day Sets',
					features: [
						{
							id: 'SUPPORT_TEXTS',
							text: 'Boost Articles',
							icon: 'info-icon',
							hint: 'Boost articles are slightly lower level articles included in Article-A-Day sets to help you further differentiate for your students.'
						},
						{
							id: 'CHALLENGE_TEXTS',
							text: 'Challenge Articles',
							icon: 'info-icon',
							hint:
								'Challenge articles are slightly higher level articles included in Article-A-Day sets to help you further differentiate for your students.'
						},
						{
							id: 'ELL',
							icon: 'info-icon',
							text: 'Collections for ELLs',
							hint: 'Topics that are concrete or may be familiar or relevant to ELL students are a great place to start your Article-A-Day routine.'
						},
						{
							id: 'DECODABLE_TEACHER_EDITIONS',
							icon: 'info-icon',
							text: 'Decodables',
							hint:
								'These nonfiction decodables align with Article-A-Day. By reading these decodables and listening to Article-A-Day, students build background knowledge while applying their decoding skills. <a href="/teacher-guide/decodable-texts.html">Learn more...</a>'
						}
					]
				},
				{
					id: PAIRED_TEXT_ID,
					text: 'Paired Texts',
					groupName: 'paired-text-subtype',
					subTypes: [
						{
							id: PAIRED_TEXT_ID,
							text: 'All'
						},
						{
							id: 8000,
							text: 'English-English'
						},
						{
							id: 8001,
							text: 'Spanish-English (for ELLs)',
							icon: 'info-icon',
							hint:
								'Spanish-English Paired Texts help English Language Learners build background knowledge in their first language before they read the English passage on a related topic.'
						}
					]
				},
				{
					// Update as more Text Sets are added
					id: TS_ID,
					text: 'Book Studies',
					subTypes: [
						{
							text: 'Book Studies',
							id: 7000
						}
					],
					gcAddonDisabled: true
				}
			];

			let audioFeatures = [
				{
					id: 'HAU',
					text: 'Human-voice Audio',
					icon: 'info-icon',
					hint:
						'All ReadWorks articles come with audio. Some of them are read aloud by real human voices, while others are read aloud by a state-of-the-art synthesized voice, Polly.'
				}
			];

			let { productDataMap, productFeaturesMap, productSubTypesMap } = productData.reduce(
				(acc, p) => {
					if (p && p.id) {
						acc.productDataMap[p.id] = p;

						if (p.features) {
							for (const feature of p.features) {
								acc.productFeaturesMap[feature.id] = Object.assign({}, feature, {
									product: p.id
								});
							}
						}

						if (p.subTypes) {
							for (const subType of p.subTypes) {
								acc.productSubTypesMap[subType.id] = Object.assign({}, subType, {
									product: p.id
								});
							}
						}
					}
					return acc;
				},
				{ productDataMap: {}, productFeaturesMap: {}, productSubTypesMap: {} }
			);

			productFeaturesMap = audioFeatures.reduce((acc, f) => {
				acc[f.id] = _.assign({}, f);
				return acc;
			}, productFeaturesMap);

			const emptyResultsMessages = {
				AADVocab:
					'While Article-A-Day vocabulary activities are only available for K-5th grades, all articles in all Article-A-Day sets have our vocabulary widget. This widget provides in-line definitions, human voice pronunciations, and word forms to support student exploration of key Tier 2 and 3 words.',
				AADVocabHS: 'Article-A-Day vocabulary activities are only available for K-5th grades.',
				AADSets:
					'While Article-A-Day sets are only available for K-8th grades, high school students can build their background knowledge and gain important reading practice from middle school Article-A-Day sets. So, please try expanding your search by selecting 6th, 7th, and/or 8th grade.',
				decodables:
					'<p>Looking for decodables?</p><p>Find our nonfiction K-2 decodable texts by using the <a href="/find-content#!contentTab:search/q:/g:/t:/pt:AAD/features:decodable_teacher_editions/at:/">decodables filter</a> under Article-A-Day or through our <a href="/find-content#!contentTab:scope-and-sequence/selectedSet:topical/q:/g:/t:/pt:/features:/">Article-A-Day Scope & Sequence</a>.</p><p>If you want to explore fiction decodable texts through our partnership with Whole Phonics, you can access those on our <a href="/teacher-guide/whole-phonics.html">partner page</a>.</p>',
				default:
					"<p>Chances are, we're busy adding exactly what you're looking for to our library…</p><p>In the meantime, please try broadening your search.</p>"
			};

			return {
				debug,
				productData,
				productDataMap,
				// productFeatures,
				productFeaturesMap,
				productSubTypesMap,
				audioFeatures,
				emptyResultsMessages,
				lessonsAndUnitsTooltip: 'These are in the Lessons & Units section. (Print-only. Books are not provided.)',
				// error flag to check if the search is down at the moment
				SEARCH_UNAVAILABLE: SEARCH_UNAVAILABLE,

				gradeSelections: [],
				textTypeSelections: [],
				textTypeById: {},
				currentSearch: $.extend(true, {}, BLANK_SEARCH),
				searchResults: Object.assign({}, EMPTY_RESULTS),
				softSearchResults: Object.assign({}, EMPTY_RESULTS),
				q: '',
				queryString: window.location.hash,
				searchOngoing: true,
				searchAborted: false,
				searchError: false,
				searchExclusions: null, // leave out of currentSearch to allow default empty state
				// list of lexile choices, with a val for what to add
				lexileChoiceData: [
					{
						text: 'NP: Non-Prose',
						id: 'NP'
					}
				],
				title: 'Find Reading Passages',
				myList: null,
				isFiltered: false,
				pinterestThumbnail,
				undoPills: [],
				mobileSearchOpen: false,
				mobileFilterOpen: false,
				filterHeight: '0px',
				resultsMinHeight: undefined,
				spotlightProducts: null,
				topicsMap: _.assign({}, metadata.TOPICS_LIST),
				collectionsMap: _.assign({}, metadata.SPECIAL_COLLECTIONS_LIST),
				activityTypesData,
				activityTypeMap,
				didYouMeanShouldClear: false,
				didYouMeanNode: null,
				SEARCH,
				SCOPE_AND_SEQUENCE,
				RMS,
				classes: [],
				recommendationsMax: 6,
				recommendationsLoading: false,
				loaderImgSrc,
				bookList: [
					{
						id: 0,
						title: 'All Books'
					},
					...metadata.books
				]
			};
		},
		modelData: {
			userContext: 'userContext',
			q: 'currentSearch.q',
			g: 'currentSearch.g',
			t: 'currentSearch.t',
			s: 'currentSearch.s',
			cid: 'currentSearch.cid',
			s0: 'currentSearch.s0',
			f: 'currentSearch.f',
			lc: 'currentSearch.lc',
			l: 'currentSearch.l',
			pt: 'currentSearch.pt',
			sr: 'currentSearch.sr',
			features: 'currentSearch.features',
			staff_picks: 'currentSearch.staff_picks',
			sel: 'currentSearch.sel',
			at: 'currentSearch.at',
			subType: 'currentSearch.subType',
			book: 'currentSearch.book',
			wc: 'currentSearch.wc',
			series: 'currentSearch.series',
			contentTab: 'contentTab',
			recommendedContent: 'recommendedContent',
			recommendationsClassCode: 'recommendationsClassCode',
			gcAddon: 'gcAddon'
		},
		methods: {
			isNewSearchValue(key, value) {
				const currentValue = this.currentSearch[key];
				return !_.isEqual(currentValue, value) && !(currentValue === undefined && value === BLANK_SEARCH[key]);
			},
			setCurrentSearchProperty(key, value) {
				if (this.isNewSearchValue(key, value)) {
					findArticlesQueue.addToQueue(() => {
						this.currentSearch[key] = value;
					})();
				}
			},
			/**
			 * Go to another page of the search results, scroll back to top
			 */
			goToPage: function (target) {
				let updatedPage = target;
				if (typeof updatedPage !== 'number') {
					updatedPage = 0;
				}
				this.currentSearch.f = updatedPage;
				this.goToResults();
			},

			goToResults: function () {
				let currentTop = this.$refs.results.getBoundingClientRect().top;
				// Using 200px as a loose tolerance if the results are visible but
				// pretty close to the bottom of the viewport
				// Mobile view causes this when all the filter fold under each other
				let userIsAboveResults = currentTop + 200 >= window.innerHeight;

				// Only allow 200px down from top of the results element before scrolling
				// Caused when a user scrolls down on the page and updates a filter.
				let userIsBelowResults = currentTop + 200 <= 0;
				if (userIsAboveResults || userIsBelowResults) {
					scrollManager(this.$refs.results, {
						priority: 2,
						duration: 500
					});
				}
			},
			showLoadMask: function () {
				this.searchOngoing = true;
			},
			hideLoadMask: function () {
				this.searchOngoing = false;
			},

			/**
			 * This function performs the actual search (making an API call) using the
			 * properties of currentSearch.
			 *
			 * @param  {Function} cb A callback to invoke after the search is complete
			 */
			findArticles(isImmediate) {
				let searchParams = {};

				this.searchResults = Object.assign({}, EMPTY_RESULTS);
				this.softSearchResults = Object.assign({}, EMPTY_RESULTS);
				this.searchAborted = false;
				this.searchError = false;
				this.isFiltered = false;

				this.showLoadMask();

				// if there was some previous search, then stop it now
				this.abortSearch();

				this.makeUndoPills();

				//Filter out unwanted / empty params
				for (let key in this.currentSearch) {
					// Filter out *0 properties (s0)
					if (key && key.indexOf('0') === -1) {
						let searchVal = this.currentSearch[key];
						// if not an array and truthy (not empty), include
						if (searchVal) {
							if (Array.isArray(searchVal)) {
								if (key === 'subType') {
									searchVal = searchVal.reduce((acc, val) => {
										if (Number.isInteger(val)) {
											acc.push(val);
										}

										return acc;
									}, []);
								}

								if (searchVal.length === 0) {
									continue;
								}
							}

							if (key === 'l' && (searchVal.length !== 2 || !searchVal.every(i => typeof i === 'number'))) {
								continue;
							}

							if (key === 'book' && !this.bookList.some(book => book.id === searchVal)) {
								// do not query for unknown books
								this.currentSearch.book = 0;
							}

							searchParams[key] = searchVal;
							this.isFiltered = true;
						}
					}
					if (key === 'q' && this.currentSearch.q !== this.q) {
						this.q = (this.currentSearch.q || '').trim();
					}
				}

				const exclusions = this.searchExclusions;
				if (Object.keys(searchParams).length > 0 && exclusions) {
					searchParams.exclusions = this.searchExclusions;
				}

				this._req = discoveryService
					.searchProduct(searchParams)
					.then(results => {
						if (!results.total) {
							results = Object.assign({}, EMPTY_RESULTS);
						}

						// whenever there are no filters, sort by danteTextId in descending order to get
						// consistent ordering
						delete searchParams.f;
						if (Object.keys(searchParams).length === 0) {
							results.hits = _.sortBy(results.hits, 'danteTextId');
						}

						this.searchResults = results;

						if (results.total < 10) {
							searchParams.soft = true;
							return discoveryService.searchProduct(searchParams);
						}
						return Promise.resolve(Object.assign({}, EMPTY_RESULTS));
					})
					.then(({ hits: softHits, total: softTotal }) => {
						if (softTotal > 0) {
							const { hits: strictHits } = this.searchResults;
							const strictUuids = new Set(strictHits.map(({ uuid }) => uuid));
							const uniqueSoftResults = softHits.filter(({ uuid }) => !strictUuids.has(uuid));

							this.softSearchResults.hits = uniqueSoftResults;
							this.softSearchResults.total = softTotal;
						}

						this.searchAborted = false;

						Vue.nextTick(() => {
							let promise = Q();

							if (isImmediate) {
								promise = this.restoreScrollPosition();
							}

							if (breakpoints.isSmallScreen()) {
								promise.then(() => this.goToResults());
							}
						});
					})
					.fin(() => {
						this._req = null;
						if (!this.recommendationsLoading) {
							this.hideLoadMask();
						}
						if (this.didYouMeanShouldClear) {
							this.didYouMeanNode = null;
						} else if (this.didYouMeanNode) {
							this.didYouMeanShouldClear = true;
						}
					});
			},
			submitSearch: function (ev) {
				let self = this;

				this.$(':focus').blur();

				this.mobileFilterOpen = false;
				this.mobileSearchOpen = false;

				if (this.q !== this.currentSearch.q) {
					this.q = (this.q || '').trim();
					self.setCurrentSearchProperty('q', this.q.trim());
					self.resetPagination();
				}
			},
			resetSearchText: function () {
				this.q = '';
				this.setCurrentSearchProperty('q', this.q);
				this.resetPagination();
			},
			isActivityFiltered(activity) {
				const filters = this.currentSearch.at;
				if (!filters || filters.length === 0) {
					return false;
				}

				return filters.indexOf(activity.id) !== -1;
			},
			onActivityClick: findArticlesQueue.addToQueue(function (activity, value) {
				let self = this;
				let filters = self.currentSearch.at;
				let id = activity && activity.id;

				if (!filters) {
					self.currentSearch.at = [];
					filters = self.currentSearch.at;
				}

				if (value === false) {
					self.removeFilterFromList(filters, id);
				} else if (value === true) {
					self.addFilterToList(filters, id);
				} else if (filters.indexOf(id) !== -1) {
					self.removeFilterFromList(filters, id);
				} else {
					self.addFilterToList(filters, id);
				}
				self.resetPagination();
			}),
			onTopicClick: findArticlesQueue.addToQueue(function (topic) {
				let self = this;
				let item = { id: topic };

				if (!self.isTopicFiltered(item)) {
					self.addTopicFilter(item);
				} else {
					self.removeTopicFilter(item);
				}

				self.resetPagination();
			}),
			isTopicFiltered(topic) {
				let self = this;
				let filters = self.currentSearch.s;

				if (!filters) {
					self.currentSearch.s = [];
					filters = self.currentSearch.s;
					return false;
				}

				if (!topic) {
					return false;
				}
				return filters.indexOf(topic.id) !== -1;
			},
			addTopicFilter(topic) {
				let self = this;
				let filters = self.currentSearch.s;

				self.currentSearch.s = [];
				filters = self.currentSearch.s;
				self.addFilterToList(filters, topic.id);
			},
			removeTopicFilter(topic) {
				let self = this;
				let filters = self.currentSearch.s;

				if (!filters) {
					self.currentSearch.s = [];
					filters = self.currentSearch.s;
				}

				self.removeFilterFromList(filters, topic.id);
			},
			onCollectionClick(collection) {
				let set = [];
				if (collection && metadata.allNodesById['_' + collection]) {
					let node = metadata.allNodesById['_' + collection];
					if (node.parentId && node.parentId !== metadata.domainNode.id) {
						set = [node.parentId, node.id];
					} else {
						set = [node.id];
					}
				}
				this.setS0(set);
				this.resetPagination();
			},
			clickResetSearch() {
				this.q = '';

				findArticlesQueue.setOptions({
					threshhold: 10,
					onQueueClear: () => {
						findArticlesQueue.setOptions({
							threshhold: 1000,
							onQueueClear: undefined
						});
					}
				});

				for (let key in BLANK_SEARCH) {
					let val = _.clone(BLANK_SEARCH[key]);
					if (key === 's0') {
						this.setS0(val);
					} else {
						this.setCurrentSearchProperty(key, val);
					}
				}
			},
			isTextTypeFiltered: function (tt) {
				let self = this;
				let filters = self.currentSearch.t;

				if (!filters) {
					self.currentSearch.t = [];
					filters = self.currentSearch.t;
					return false;
				}

				if (!tt) {
					return false;
				}
				return filters.indexOf(tt.id) !== -1;
			},
			addTextTypeFilter: function (tt) {
				let self = this;
				let filters = self.currentSearch.t;

				if (!filters) {
					self.currentSearch.t = [];
					filters = self.currentSearch.t;
				}

				self.addFilterToList(filters, tt.id);
			},
			removeTextTypeFilter: function (tt) {
				let self = this;
				let filters = self.currentSearch.t;

				if (!filters) {
					self.currentSearch.t = [];
					filters = self.currentSearch.t;
				}

				self.removeFilterFromList(filters, tt.id);
			},
			clearAllTextTypeSelections: function (ev) {
				let self = this;
				let textTypes = self.textTypes;

				textTypes.forEach(tt => self.removeTextTypeFilter(tt));

				self.resetPagination();
			},
			toggleTextTypeSelection: findArticlesQueue.addToQueue(function (tt) {
				let self = this;

				if (self.isTextTypeFiltered(tt)) {
					self.removeTextTypeFilter(tt);
				} else {
					self.addTextTypeFilter(tt);
				}

				self.resetPagination();
			}),
			clearAllProductSelections: function () {
				let self = this;
				let products = self.productData;

				products.forEach(product => self.removeProductFilter(product));

				self.resetPagination();
			},
			toggleProductSelection: findArticlesQueue.addToQueue(function (product, value) {
				let self = this;
				let productDataMap = self.productDataMap;

				if (productDataMap[product.id]) {
					let p = productDataMap[product.id];
					if (typeof value === 'boolean') {
						if (value) {
							self.addProductFilter(p);
						} else {
							self.removeProductFilter(p);
						}
					} else if (self.isProductFiltered(p)) {
						self.removeProductFilter(p);
					} else {
						self.addProductFilter(p);
					}
				}

				self.resetPagination();
			}),
			toggleFeatureSelection: findArticlesQueue.addToQueue(function (feature, value) {
				let self = this;
				let productFeaturesMap = this.productFeaturesMap;

				if (productFeaturesMap[feature.id]) {
					let f = productFeaturesMap[feature.id];
					if (typeof value === 'boolean') {
						if (value) {
							self.addFeatureFilter(f);
							if (f.product && !self.isProductFiltered(f.product)) {
								self.addProductFilter({ id: f.product });
							}
						} else {
							self.removeFeatureFilter(f);
						}
					} else if (self.isFeatureFiltered(f)) {
						self.removeFeatureFilter(f);
					} else {
						self.addFeatureFilter(f);
						if (f.product && !self.isProductFiltered(f.product)) {
							self.addProductFilter({ id: f.product });
						}
					}
				}

				self.resetPagination();
			}),
			isSubTypeFiltered(subType) {
				return (this.currentSearch.subType || []).includes(subType);
			},
			setSubType({ id: subType }, value) {
				if (!this.currentSearch.subType) {
					this.currentSearch.subType = [];
				}
				const filter = this.currentSearch.subType;

				if (value === true) {
					const subTypeData = this.productSubTypesMap[subType];
					const productId = subTypeData ? subTypeData.product : subType;
					if (!this.isProductFiltered({ id: productId })) {
						this.addProductFilter({ id: productId });
					}

					this.addFilterToList(filter, subType);
				} else {
					this.removeFilterFromList(filter, subType);
				}

				this.resetPagination();
			},
			toggleSubTypeSelection: findArticlesQueue.addToQueue(function (subType, value) {
				this.setSubType(subType, value);
			}),
			selectBook: findArticlesQueue.addToQueue(function (book) {
				this.currentSearch.book = book;
			}),
			abortSearch: function () {
				if (this._req && typeof this._req.abort === 'function') {
					this._req.abort();
				}
			},
			addFilterToList: function (list, filter, getIndex) {
				if (!getIndex) {
					getIndex = (arr, f) => arr.indexOf(f);
				}

				if (list && filter && getIndex(list, filter) === -1) {
					list.push(filter);
				}
				return list;
			},
			removeFilterFromList: function (list, filter, getIndex) {
				if (!getIndex) {
					getIndex = (arr, f) => {
						let itemIdx = -1;
						arr.some((a, ax) => {
							if (a.toString() === f.toString()) {
								itemIdx = ax;
								return true;
							}
							return false;
						});
						return itemIdx;
					};
				}

				if (!filter || !list) return undefined;

				let idx = getIndex(list, filter);
				if (idx !== -1) {
					list.splice(idx, 1);
				}
				return list;
			},
			addStepReadsFilter: function () {
				var self = this;
				self.currentSearch.sr = true;
			},
			removeStepReadsFilter: function () {
				var self = this;
				self.currentSearch.sr = false;
			},
			isStepReadsFiltered: function () {
				return this.currentSearch.sr;
			},
			isFeatureFiltered: function (feature) {
				let self = this;
				let filters = self.currentSearch.features;

				if (!filters) {
					self.currentSearch.features = [];
					filters = self.currentSearch.features;
					return false;
				}

				if (!feature) {
					return false;
				}

				if (feature.id.toLowerCase() === 's') {
					return self.isStepReadsFiltered();
				} else {
					return filters.indexOf(feature.id.toLowerCase()) !== -1;
				}
			},
			addFeatureFilter: function (feature) {
				let self = this;

				if (feature.id.toLowerCase() === 's') {
					self.addStepReadsFilter();
				} else {
					let filters = self.currentSearch.features;

					if (!filters) {
						self.currentSearch.features = [];
						filters = self.currentSearch.features;
					}

					self.addFilterToList(filters, feature.id.toLowerCase());
				}
			},
			removeFeatureFilter: function (feature) {
				let self = this;

				if (feature.id.toLowerCase() === 's') {
					self.removeStepReadsFilter();
				} else {
					let filters = self.currentSearch.features;

					if (!filters) {
						self.currentSearch.features = [];
						filters = self.currentSearch.features;
					}

					self.removeFilterFromList(filters, feature.id.toLowerCase());
				}
			},
			isProductFiltered: function (product) {
				let self = this;
				let filters = self.currentSearch.pt;

				if (!filters) {
					self.currentSearch.pt = [];
					filters = self.currentSearch.pt;
					return false;
				}

				if (!product) {
					return false;
				}

				return filters.indexOf(product.id) !== -1;
			},
			addProductFilter(product) {
				let filters = this.currentSearch.pt;

				if (!filters) {
					this.currentSearch.pt = [];
					filters = this.currentSearch.pt;
				}
				this.addFilterToList(filters, product.id);

				if (product.subTypes) {
					filters = this.currentSearch.subType;

					if (product.id === TS_ID) {
						this.currentSearch.book = 0;
					}

					this.setSubType(product.subTypes[0], true);
				}
			},
			removeProductFilter(product) {
				let filters = this.currentSearch.pt;

				if (!filters) {
					this.currentSearch.pt = [];
					filters = this.currentSearch.pt;
				}

				this.removeFilterFromList(filters, product.id);
				if (product.features) {
					product.features.forEach(f => this.removeFeatureFilter(f));
				}

				if (product.subTypes) {
					product.subTypes.forEach(st => {
						this.setSubType(st, false);
					});

					if (product.id === TS_ID) {
						this.currentSearch.book = 0;
					}
				}
			},
			isGradeSelected: function (grade) {
				let self = this;
				let filters = self.currentSearch.g;

				if (!filters) {
					self.currentSearch.g = [];
					filters = self.currentSearch.g;
					return false;
				}

				if (!grade) {
					return false;
				}
				return filters.indexOf(grade.id) !== -1;
			},
			isGradeOnlyInSelectedRange(grades) {
				const filters = this.currentSearch.g;

				if (filters.length === 0) {
					return false;
				}

				for (let i = 0; i < filters.length; i++) {
					if (!grades.has(filters[i])) {
						return false;
					}
				}

				return true;
			},
			addGradeFilter: function (grade) {
				let self = this;
				let filters = self.currentSearch.g;

				if (!filters) {
					self.currentSearch.g = [];
					filters = self.currentSearch.g;
				}

				self.addFilterToList(filters, grade.id);
			},
			removeGradeFilter: function (grade) {
				let self = this;
				let filters = self.currentSearch.g;

				if (!filters) {
					self.currentSearch.g = [];
					filters = self.currentSearch.g;
				}

				self.removeFilterFromList(filters, grade.id);
			},
			clearGrades: function (grade) {
				let self = this;

				self.setCurrentSearchProperty('g', []);
				self.resetPagination();
			},
			toggleGrade: findArticlesQueue.addToQueue(function (grade) {
				let self = this;

				if (self.isGradeSelected(grade)) {
					self.removeGradeFilter(grade);
				} else {
					self.addGradeFilter(grade);
				}

				self.resetPagination();
			}),
			setS0: function (ids = [], immediate) {
				let self = this;
				let filterSet = {
					cid: [],
					staff_picks: [],
					sel: [],
					series: []
				};
				let filterKey = null;
				let filterNode = null;
				let parentId = ids.length > 0 ? ids[0] : null;
				let childId = ids.length === 2 ? ids[1] : null;
				let parentNode = null;
				let childNode = null;

				self.setCurrentSearchProperty('s0', ids);
				if (parentId) {
					parentNode = metadata.allNodesById['_' + parentId];
					filterNode = parentNode;
					let parentFilterInfo = PARENT_WHITELIST[parentId];
					filterKey = parentFilterInfo.filterKey;
					if (childId) {
						childNode = metadata.allNodesById['_' + childId];
						filterNode = childNode;
					}
					if (filterSet[filterKey] && filterNode && filterNode.id) {
						filterSet[filterKey].push(filterNode.id);
					}
				}

				if (immediate) {
					_.assign(self.currentSearch, filterSet);
				} else {
					self.setCurrentSearchProperty('cid', filterSet.cid);
					self.setCurrentSearchProperty('staff_picks', filterSet.staff_picks);
					self.setCurrentSearchProperty('sel', filterSet.sel);
					self.setCurrentSearchProperty('series', filterSet.series);
				}
			},
			isLexileChoiceSelected: function (lexileChoice) {
				let self = this;
				let filters = self.currentSearch.lc || [];

				return filters.indexOf(lexileChoice.id) !== -1;
			},
			toggleLexileChoice: findArticlesQueue.addToQueue(function (lexileChoice) {
				let self = this;
				let filters = this.currentSearch.lc;

				if (!filters) {
					this.currentSearch.lc = [];
					filters = this.currentSearch.lc;
				}

				if (this.isLexileChoiceSelected(lexileChoice)) {
					self.removeFilterFromList(filters, lexileChoice.id);
				} else {
					self.addFilterToList(filters, lexileChoice.id);
				}
			}),
			clearLexiles: function () {
				this.setCurrentSearchProperty('l', BLANK_SEARCH.l.slice());
				this.setCurrentSearchProperty('lc', BLANK_SEARCH.lc.slice());
				this.resetPagination();
			},
			onRangeChange: findArticlesQueue.addToQueue(function (range, property) {
				let updatedRange = [];

				if (range && range.length === 2) {
					updatedRange = range;
				}

				this.setCurrentSearchProperty(property, updatedRange);

				this.resetPagination();
			}),
			resetPagination: function () {
				if (this.currentSearch.f !== 0) {
					this.setCurrentSearchProperty('f', 0);
				}
			},
			toggleOnList: function (product) {
				if (!this.myList) {
					return undefined;
				}
				var productId = product.uuid;
				let inMyList = this.myList.some(l => l.productId === productId);

				if (inMyList) {
					fireEvent(REMOVE_FROM_BINDER, {
						p: productId
					});

					return crudService.remove('myList', productId);
				} else {
					// if product ID contains version, strip it out and just use the UUID
					if (utilities.productIdHasVersion(productId)) {
						var pv = utilities.parseProductUUIDAndVersion(productId);
						productId = pv.uuid;
					}

					fireEvent(ADD_TO_BINDER, {
						p: productId
					});

					return crudService.create('myList', {
						productId: productId
					});
				}
			},
			getMyList: function () {
				if (this.userContext) {
					crudService.read('myList').then(list => (this.myList = list));
				}
			},
			setSearchParam: function () {
				model.set('searchResultQueryString', this.queryString);
			},
			makeUndoPills: function () {
				var self = this;
				var p = [];

				if (self.currentSearch.q) {
					p.push({
						name: '"' + self.currentSearch.q + '"',
						isKeywordOption: true,
						undo: function () {
							self.currentSearch.q = '';
							self.q = '';
							self.setCurrentSearchProperty('f', 0);
						}
					});
				}

				if (self.currentSearch.s && self.currentSearch.s.length) {
					let topicId = self.currentSearch.s[0];
					let node = metadata.allNodesById['_' + topicId];
					if (topicId && node) {
						let name = node.name;
						p.push({
							name,
							undo: function () {
								self.setCurrentSearchProperty('s', []);
								self.setCurrentSearchProperty('f', 0);
							}
						});
					}
				}

				// _.each(self.currentSearch.s0, function(domainId) {
				if (self.currentSearch.s0 && self.currentSearch.s0.length) {
					let domainId = self.currentSearch.s0[self.currentSearch.s0.length - 1];
					// Pills use all nodes (active + inactive)
					// To allow for unfiltering mechanism
					if (!metadata.allNodesById['_' + domainId]) {
						return;
					}
					var domainName = metadata.allNodesById['_' + domainId].name;
					p.push({
						name: domainName,
						undo: function () {
							self.setS0([]);
							self.setCurrentSearchProperty('f', 0);
						}
					});
				}
				// });

				_.each(self.gradeSelections, function (gs) {
					if (self.isGradeSelected(gs)) {
						p.push({
							name: 'Grade ' + gs.text,
							undo: function () {
								self.removeGradeFilter(gs);
								self.setCurrentSearchProperty('f', 0);
							}
						});
					}
				});

				_.each(self.textTypeSelections, function (tt) {
					if (self.isTextTypeFiltered(tt)) {
						p.push({
							name: tt.text,
							undo: function () {
								self.removeTextTypeFilter(tt);
								self.setCurrentSearchProperty('f', 0);
							}
						});
					}
				});

				_.each(self.products, function (ps) {
					if (ps.selected) {
						p.push({
							name: ps.text,
							undo: function () {
								self.removeProductFilter(ps);
								self.setCurrentSearchProperty('f', 0);
							}
						});
						if (ps.features) {
							ps.features.forEach(f => {
								if (f.selected) {
									p.push({
										name: f.text,
										undo: function () {
											self.removeFeatureFilter(f);
											self.setCurrentSearchProperty('f', 0);
										}
									});
								} else if (f.children) {
									let allSelected = f.children.every(c => c.selected);
									if (allSelected && f.allText) {
										p.push({
											name: f.allText,
											undo() {
												f.children.forEach(c => self.removeFeatureFilter(c, false));
											}
										});
									} else {
										f.children.forEach(c => {
											if (c.selected) {
												p.push({
													name: c.text,
													undo: function () {
														self.removeFeatureFilter(c);
														self.setCurrentSearchProperty('f', 0);
													}
												});
											}
										});
									}
								}
							});
						}

						if (ps.subTypes && ps.subTypes.length > 1) {
							ps.subTypes.forEach(subType => {
								if (subType.selected && subType.id !== ps.id) {
									p.push({
										name: subType.text,
										undo() {
											self.setSubType(subType, false);
											self.setSubType(ps.subTypes[0], true);
										}
									});
								}
							});
						}
					}
				});

				_.each(self.audio, f => {
					if (f.selected) {
						p.push({
							name: f.text,
							undo: function () {
								self.removeFeatureFilter(f);
								self.setCurrentSearchProperty('f', 0);
							}
						});
					}
				});

				_.each(self.activityTypes, f => {
					if (f.children) {
						f.children.forEach(c => {
							if (c.selected) {
								p.push({
									name: c.id === f.id ? f.text : c.text,
									undo: function () {
										self.onActivityClick(c, false);
										self.setCurrentSearchProperty('f', 0);
									}
								});
							}
						});
					} else if (f.selected) {
						p.push({
							name: f.text,
							undo: function () {
								self.onActivityClick(f, false);
								self.setCurrentSearchProperty('f', 0);
							}
						});
					}
				});

				if (self.currentSearch.lc && self.currentSearch.lc.length && self.currentSearch.lc[0] === 'NP') {
					p.push({
						name: 'Non-Prose',
						undo: function () {
							self.currentSearch.lc = [];
						}
					});
				}
				if (self.currentSearch.l && self.currentSearch.l.length === 2) {
					let lexileRange = self.currentSearch.l;
					p.push({
						name: lexileRange[0] + 'L to ' + lexileRange[1] + 'L',
						undo: function () {
							self.currentSearch.l = [];
						}
					});
				}

				const wordCounts = self.currentSearch.wc;
				if (wordCounts && wordCounts.length === 2) {
					const [minCount, maxCount] = wordCounts;
					let wordCountName = `${minCount} - ${maxCount} Words`;

					if (maxCount === 3000) {
						wordCountName = `${minCount}+ Words`;
					}

					p.push({
						name: wordCountName,
						undo() {
							self.currentSearch.wc = [];
						}
					});
				}

				const book = self.currentSearch.book;
				if (book) {
					const selectedBook = self.bookList.find(b => b.id === book);

					if (selectedBook) {
						const bookTitle = selectedBook.title;
						p.push({
							name: bookTitle,
							undo() {
								self.currentSearch.book = 0;
							}
						});
					}
				}

				self.undoPills = p;
			},
			onSearchInput(val) {
				this.q = val;
			},
			suggestionCategoryFormatter(obj) {
				let category = obj.parentName;
				if (category === 'Domain') {
					category = 'Topic';
				}
				return category;
			},
			onSuggestionClick(node, keywordText) {
				if (node) {
					let id = node.id;
					let parentId = node.parentId;
					let keyword = node.keyword;
					let query = this.q;

					fireEvent(SEARCH_SUGGESTION_CLICK, {
						id,
						parentId,
						query,
						keyword
					});
					this.applyFilters(node);
					this.mobileSearchOpen = false;

					if (keywordText) {
						this.didYouMeanShouldClear = false;
						this.didYouMeanNode = _.assign(
							{
								keyword: keywordText
							},
							node
						);
					}
				}
			},
			applyFilters(...nodes) {
				nodes = nodes || [];
				this.didYouMeanNode = null;

				nodes.forEach(node => {
					let id = node.id;
					let parentId = node.parentId;
					let keyword = node.keyword;

					if (id) {
						// evaluate books first since book ids can conflict with others
						if (parentId === 'book') {
							const bookStudyProduct = this.productDataMap[TS_ID];

							if (this.isProductFiltered(bookStudyProduct)) {
								if (this.currentSearch.book === id) {
									this.toggleProductSelection(bookStudyProduct, false);
									this.selectBook(0);
								} else {
									this.selectBook(id);
								}
							} else {
								this.toggleProductSelection(bookStudyProduct, true);
								this.selectBook(id);
							}
						} else if (this.activityTypeMap[`at_${id}`]) {
							if (!this.isActivityFiltered(this.activityTypeMap[`at_${id}`].activity)) {
								// de-select any siblings
								if (this.activityTypeMap[`at_${id}`].group) {
									this.activityTypeMap[`at_${id}`].group.forEach(sisterActivity => {
										this.onActivityClick(sisterActivity, false);
									});
								}

								this.onActivityClick(this.activityTypeMap[`at_${id}`].activity, true);
							} else {
								this.onActivityClick(this.activityTypeMap[`at_${id}`].activity, false);
							}
						} else if (this.productDataMap[id]) {
							if (!this.isProductFiltered(node)) {
								this.toggleProductSelection(node, true);
							} else {
								this.toggleProductSelection(node, false);
							}
						} else if (this.productFeaturesMap[id]) {
							if (!this.isFeatureFiltered(node)) {
								this.toggleFeatureSelection(node, true);
							} else {
								this.toggleFeatureSelection(node, false);
							}
						} else if (this.textTypeById['_' + id]) {
							this.toggleTextTypeSelection(node);
						} else if ((parentId && parentId === metadata.domainNode.id) || this.topicsMap[parentId]) {
							this.onTopicClick(id);
						} else if ((parentId && this.collectionsMap[parentId]) || (id && this.collectionsMap[id])) {
							let set = [id];
							if (parentId) {
								set = [parentId].concat(set);
							}
							if (JSON.stringify(set) === JSON.stringify(this.currentSearch.s0)) {
								this.setS0([]);
							} else {
								this.setS0(set);
							}
						}
					} else if (keyword) {
						this.submitSearch();
					}
				});
			},
			onFeaturedContentClick(data) {
				fireEvent(PRODUCT_PROMOTION_CLICK, data);
				this.setSearchParam();
			},
			onMobileFilterApply() {
				this.mobileFilterOpen = false;
				this.mobileSearchOpen = false;
				this.findArticles();
			},
			onMobileSearchApply() {
				this.mobileFilterOpen = false;
				this.mobileSearchOpen = false;
				this.submitSearch();
			},
			onMobileFilterClick() {
				this.onWindowScroll();
				this.mobileFilterOpen = !this.mobileFilterOpen;
				this.mobileSearchOpen = false;
			},
			onMobileSearchClick() {
				this.onWindowScroll();
				this.mobileSearchOpen = !this.mobileSearchOpen;
				this.mobileFilterOpen = false;
			},
			onWindowScroll: _.debounce(function () {
				let top = 0;
				let header = this.$refs.mobileHeader;
				let rect = header ? header.getBoundingClientRect() : null;
				if (rect) {
					top = rect.bottom - 1;
				}
				this.filterHeight = top + 'px';

				const filtersContainer = this.$refs.filters;

				if (filtersContainer) {
					const filtersContainerHeight = Math.ceil(filtersContainer.getBoundingClientRect().height);

					this.resultsMinHeight = `calc(${filtersContainerHeight}px + 100vh)`;
				}
			}, 25),
			onDidYouMeanClick() {
				let node = this.didYouMeanNode;
				if (node && node.keyword) {
					// undo node match
					this.applyFilters(node);

					// apply keyword query
					this.q = node.keyword;
					this.submitSearch();
				}
			},
			changeSearchView(view, opts) {
				const tab = searchTabs[view];
				const userContext = this.userContext;
				if (tab) {
					if (tab.auth && !userContext) {
						let href = '/find-content#!';
						if (tab && tab.key) {
							href += 'contentTab:' + tab.key;
						}
						if (opts) {
							Object.keys(opts).forEach(key => {
								href += `/${key}:${opts[key]}`;
							});
						}
						page('/?url=' + encodeURIComponent(href) + '#login');
					} else {
						// page(href) is broken
						// Setting the model keys through page causes an exception
						page('/find-content');
						model.set('contentTab', tab.key);
						if (opts) {
							Object.keys(opts).forEach(key => {
								model.set(key, opts[key]);
							});
						}
					}
				}
			},
			showLoginModal() {
				utilities.openModal('#teacher-login-modal');
			},
			initRecommendations() {
				this.recommendationsLoading = true;

				if (!this.recommendedContent) {
					this.recommendedContent = {};
				}

				classUtilities.loadClasses().then(classes => {
					const activeClasses = (classes || []).filter(c => c.status === classUtilities.STATUS_MAP.active);

					this.classes = activeClasses || [];
					const classCode = this.recommendationsClassCode || (activeClasses[0] && activeClasses[0].classCode);
					this.getRecommendationsForClass(classCode);
				});
			},
			getRecommendationsForClass(classCode) {
				const clearLoadingState = () => {
					this.recommendationsLoading = false;
					this.hideLoadMask();
				};

				if (classCode) {
					this.recommendationsClassCode = classCode;
					this.recommendationsLoading = true;

					if (this.recommendedContent[classCode]) {
						clearLoadingState();
						return;
					}

					discoveryService
						.getClassRecommendations({
							cc: classCode
						})
						.then(recommendations => {
							const { sortedRecommendations, promises } = recommendations
								.sort((a, b) => a.order - b.order)
								.reduce(
									(acc, item) => {
										const { content_entity_id: pid, recommendation_type: type } = item;

										if (acc.pids.has(pid) || acc.promises.length === this.recommendationsMax) {
											return acc;
										}

										acc.pids.add(pid);
										acc.sortedRecommendations.push({
											pid,
											type
										});
										acc.promises.push(productService.get(pid));

										return acc;
									},
									{
										pids: new Set(),
										sortedRecommendations: [],
										promises: []
									}
								);

							if (recommendations.length > 0) {
								startJourney(JOURNEYS.RECOMMENDATIONS);
							}

							return Promise.all(promises).then(articles => {
								this.recommendedContent = Object.assign({}, this.recommendedContent, {
									[classCode]: {
										articles,
										sortedRecommendations
									}
								});
								clearLoadingState();
							});
						});
				} else {
					clearLoadingState();
				}
			}
		}
	};
</script>
<style lang="scss">
	@import 'config/colors';
	@import 'config/variables';
	@import 'mixins/flexbox';
	@import 'mixins/media';
	@import 'mixins/typography';

	$space-between-filtersets: 1.5rem;
	$space-between-filters: 0.25rem;

	main.main.find-articles {
		.main-content.wrapper {
			padding-top: 0;
			padding-bottom: 0;
		}

		.tab-wrapper {
			background-color: $c-blue-lighter;
			border-bottom: 1px solid $c-grey;
		}

		.form-row-input {
			&.products,
			&.audio,
			&.activity-types {
				.input-item-with-label {
					margin-bottom: $space-between-filters;
				}
			}
		}

		.clear-filter {
			display: inline-block;
			position: absolute;
			top: 0;
			right: 0;
			padding-left: $space-between-filters;
			cursor: pointer;
			color: $c-white;
			font-size: 0.75rem;
			padding-top: 0.25rem;
			vertical-align: top;

			&:hover {
				text-decoration: underline;
			}
		}

		.book-filter {
			max-width: 12rem;
			text-overflow: ellipsis;
		}

		.search-results {
			position: relative;

			.search-results-filters {
				display: flex;
				position: fixed;
				flex-direction: column;
				position: fixed;
				left: -40rem;
				top: 0;
				bottom: 0;
				background-color: $c-blue-dark;
				color: $c-white;
				z-index: 2;
				padding: 0;

				transition: left 0.3s;

				.filter-container {
					flex: 1;
					overflow-y: auto;
					padding: 1rem;
				}

				&.open {
					left: 0;
				}

				.filter-header-label,
				.range-slider-legend,
				.item-selector-legend {
					margin-bottom: 0.5rem;
					font-weight: 700;
					text-transform: uppercase;
				}

				.item-label,
				.topic.label,
				.lexile-value,
				.apply-filter-button {
					color: $c-white;
				}

				.grade-selector {
					.input-label {
						background-color: transparent;

						&.active:not(:hover) {
							background-color: $c-babyblue;

							.item-label {
								color: inherit;
							}
						}

						&:hover,
						&.active:hover {
							.item-label {
								color: $c-white;
							}
						}
					}
				}

				.topic.label.active {
					font-weight: 500;
					color: $c-babyblue;
				}

				input + label,
				.label {
					&:hover {
						color: $c-babyblue;
					}
				}

				.form-row-input {
					margin-bottom: $space-between-filtersets;
				}

				.legacy-link {
					display: block;
					font-size: 0.75rem;
					margin-bottom: $space-between-filtersets;
					margin-top: 2rem;
					padding-top: 1rem;
					border-top: 1px solid $c-grey;
					color: $c-white;

					&:hover {
						text-decoration: underline;
					}
				}

				.range-slider {
					margin-bottom: $space-between-filters;
					max-width: 24rem;

					@include media(med, max) {
						--thumb-background-color: #{$c-blue-dark};
						--thumb-border-color: #{$c-white};
						--thumb-border-width: 5px;

						.slider-container {
							margin-left: 1.25rem;
							margin-right: 1.25rem;
						}
					}
				}

				.apply-filter-button {
					text-align: center;
					cursor: pointer;
					bottom: 0;
					left: 0;
					right: 0;
					background-color: $c-dark-teal;
					padding: 1rem 2rem;
				}

				input.input-checkbox[type='checkbox'] {
					background-color: $c-blue-dark;
					border-color: $c-babyblue;
					border-radius: 4px;

					&:checked {
						background-color: $c-white;
						border-color: $c-white;

						&:after {
							content: '';
						}
					}
				}

				svg.info-icon {
					color: $c-white;
					fill: $c-white;
					stroke: $c-white;
				}
			}

			.did-you-mean {
				margin-bottom: 1rem;

				.did-you-mean-keyword {
					cursor: pointer;
					text-decoration: underline;
					font-weight: bold;
				}
			}
		}

		.empty-results {
			font-size: 1.17rem;
			font-weight: 700;
		}

		.soft-search-message {
			position: relative;
			display: flex;
			align-items: center;
			margin: 2rem 0;
			padding: 0.5rem 2rem 0.5rem 3.5rem;
			border-radius: 4px;
			background-color: $c-grey-extra-light;
			color: $c-slate-dark;

			@include media(med) {
				padding-left: 7.5rem;
			}
		}

		.soft-search-icon {
			position: absolute;
			height: 2.5rem;
			left: 0.5rem;

			@include media(med) {
				height: 6.5rem;
			}
		}

		.soft-search-heading {
			font-weight: 700;
			font-size: 1.125rem;
		}

		.main-header-mobile-toggle {
			position: sticky;
			top: 0;
			z-index: 1;
			display: flex;
			color: $c-white;
			background-color: $c-blue-dark-2;

			> .filter-toggle,
			> .search-toggle {
				flex: 1;
				text-align: center;
				padding: 0.75rem 0 0.5rem 0;

				i {
					font-size: 1.25rem;
				}
			}

			> .filter-toggle {
				background-color: $c-blue-dark;
			}
		}

		.main-header {
			background-color: $c-blue-dark-2;
			border-bottom: none;
			padding: 0;

			.wrapper {
				width: 100%;
			}

			.main-header-search {
				display: none;

				&.open {
					position: fixed;
					left: 0;
					right: 0;
					display: block;
					z-index: 5;
				}

				.main-header-search-form {
					margin-bottom: 0.75rem;
					margin-top: 0;
					padding: 1rem;
					background-color: $c-blue-dark-2;

					.search-box {
						display: flex;

						.search-bar-with-suggestions {
							flex: 1;

							input.input.validated-input {
								padding: 0.5rem 1rem;
								min-height: 2rem;
								border-color: $c-blue-dark-2;
								background-color: $c-blue-dark-2;
								color: $c-white;

								&::placeholder {
									color: white;
								}
							}
						}

						.search-action {
							color: $c-slate-dark;

							&.icon-search {
								display: none;
								right: 0.5rem;
							}

							&.fa-circle-xmark {
								color: $c-white;
								right: 2rem;
								margin-right: 0.5rem;
							}

							&:hover {
								color: $c-slate-dark;
							}

							&.mobile-search {
								font-size: 0.75rem;
								font-weight: 500;
								text-transform: uppercase;
								color: $c-white;
							}
						}
					}
				}

				.main-header-search-after {
					display: none;

					.search-results-header-count {
						text-align: right;
						margin: 0.5rem 0;
					}
				}
			}
		}

		.search-results-content {
			display: flex;
			flex-direction: column;
			padding: 1.5rem 0;

			.butter-bar {
				margin-bottom: 0.5rem;

				h3 {
					font-weight: 700;
				}
			}

			.articles .article-search-result {
				margin-bottom: 1rem;
			}

			.product-promotion {
				> h2 {
					text-align: center;
				}
			}
		}

		.test-result-checker {
			height: 0;
			opacity: 0;
		}

		.share-button-container {
			flex: 1;
			display: flex;
			flex-direction: column;
			justify-content: flex-end;

			.share-buttons.share-glyphs {
				text-align: center;
				margin: 1rem 0;
			}
		}

		.scope-and-sequence,
		.student-knowledge-inventory-about-content {
			header.main-header {
				background-color: $c-blue-lighter;
				color: $c-slate-dark;
				padding: 0.5rem 1rem;
				border-bottom: 1px solid $c-grey;
			}
		}
	}

	.product-promotion-panel-container {
		display: flex;
		flex-wrap: wrap;
		border: 1px solid $c-grey;
		border-radius: 4px;
		padding-bottom: 1rem;
		margin-bottom: 2rem;

		.product-promotion-panel {
			display: flex;
			flex-direction: column;
			justify-content: center;
			padding-bottom: 0;
			margin-bottom: 0;
			border-bottom: none;
			flex: 5;
			padding: 0.5rem 0.5rem 0.5rem 1rem;
			min-width: 18rem;

			h2 {
				text-align: left;
			}

			.promotion-blurb {
				text-align: left;
			}
		}

		.product-promotion-carousel {
			flex: 4;
			min-width: 18rem;

			a.article.article-result-content .pill {
				display: none;
			}
		}

		.carousel-border-container {
			border-left: 1px solid $c-grey;
			padding-left: 0.5rem;
			padding-right: 0.5rem;

			> h3 {
				font-weight: normal;
				margin-left: 1rem;
			}
		}
	}

	.promotion-panel {
		border-bottom: 1px solid $c-grey;
		padding-bottom: 2rem;
		margin-bottom: 2rem;

		h2 {
			text-align: center;

			&.has-sub-heading {
				margin-bottom: 0;
			}
		}

		.sub-heading {
			text-align: center;
			color: $c-slate-muted;
			margin-bottom: 1rem;
		}

		.promotion-blurb {
			text-align: center;
			margin-bottom: 1rem;
			font-size: 1rem;
		}

		.promotion-actions {
			text-align: center;
			margin-top: 1rem;

			button {
				margin-bottom: 1rem;
			}
		}

		.promotion-content {
			margin: 1rem auto;
			max-width: 30rem;
		}

		.promotion-title {
			display: flex;
			align-items: center;

			h2 {
				margin: 0.5rem 1.5rem 0.5rem 0;
			}
		}

		.article-features {
			margin-top: 1rem;

			.feature-row {
				display: flex;
				flex-wrap: wrap;

				.article-feature {
					margin-bottom: 1rem;
					margin-right: 0.5rem;
					padding: 0.25rem 0.4rem;
					display: flex;
					align-items: center;
					color: $c-blue;
					font-size: 0.75rem;

					svg,
					path {
						height: 20px;
						width: 20px;
						margin: 0 0.5rem 0 0;
						stroke: $c-blue;
						fill: $c-blue;

						text {
							stroke: $c-blue;
							fill: $c-blue;
						}

						&.ebook-icon {
							stroke-width: 1px;
						}
					}

					&:hover {
						color: $c-white;

						svg,
						path {
							stroke: $c-white;
							fill: $c-white;

							text {
								stroke: $c-white;
								fill: $c-white;
							}
						}
					}
				}
			}
		}
	}

	.featured-topics,
	.promotion-scope-and-sequence {
		.featured-topic-row {
			@include list-unstyled();
			text-align: center;
			vertical-align: top;

			li {
				display: inline-block;
			}

			.featured-topic {
				vertical-align: top;
				display: inline-block;
				height: auto;
				padding: 0.4rem 0;
				width: 8rem;
				margin: 0.5rem 0.5rem;
				text-align: center;
				border-radius: 4px;

				.article-image .article-featured-image-container {
					width: fit-content;
					height: fit-content;

					figure {
						width: 6rem;
						height: 6rem;
						border-radius: 4px;
					}
				}

				.featured-topic-name {
					color: $c-blue;
				}

				&:hover {
					cursor: pointer;
					background-color: $c-blue-highlight;

					.featured-topic-name {
						color: $c-slate-dark;
					}
				}
			}

			.scope-and-sequence-featured-topic {
				.article-image .article-featured-image-container {
					width: fit-content;
					height: fit-content;

					figure {
						width: 85px;
						height: 85px;
						border-radius: 4px;
					}
				}

				.featured-topic-name {
					color: $c-blue;
				}

				&:hover {
					cursor: pointer;
					background-color: $c-blue-highlight;

					.featured-topic-name {
						color: $c-slate-dark;
					}
				}
			}
		}
	}

	@media screen and (min-width: $bp-width-med) {
		main.main.find-articles {
			.main-header-mobile-toggle {
				display: none;
				height: 0;
				width: 0;
			}

			.main-header {
				background-color: $c-blue-lighter;
				border-bottom: 1px solid $c-grey;
				padding-top: 0.5em;

				.wrapper {
					width: 96%;
				}

				.main-header-search,
				.main-header-search.open {
					display: block;
					top: 0 !important;
					position: relative;

					.main-header-search-form {
						margin-top: 0.25rem;
						margin-bottom: 0.75rem;
						padding: 0;
						background-color: transparent;

						.search-box {
							display: block;

							.search-bar-with-suggestions {
								flex: 1;

								input.input.validated-input {
									border-color: $c-grey;
									background-color: $c-white;
									color: $c-slate-dark;

									&::placeholder {
										color: $c-slate-dark;
									}
								}
							}

							.search-action {
								position: absolute;
								bottom: 0;
								top: 0.25rem;
								color: $c-slate-dark;

								&.icon-search {
									display: inline-block;
									bottom: 0;
									top: 0.25rem;
								}

								&.fa-circle-xmark {
									color: $c-slate-dark;
									right: 2rem;
									margin-right: 0.5rem;
								}

								&:hover {
									color: $c-slate-dark;
								}

								&.mobile-search {
									display: none;
								}
							}
						}
					}

					.main-header-search-after {
						display: block;
					}
				}
			}

			.main-content .search-results {
				display: flex;

				.search-results-filters {
					top: 0 !important;
					display: block;
					position: relative;
					left: auto;
					max-width: 21rem;
					min-width: 15rem;
					margin-right: 1rem;
					padding: 1.5rem 1rem 1.5rem 0;
					border-right: 1px solid $c-grey;
					background-color: $c-white;

					.item-label,
					.topic.label,
					.range-label,
					.lexile-value,
					.apply-filter-button {
						color: $c-slate-dark;
					}

					input + label,
					.label {
						&:hover {
							color: $c-blue-dark;
						}
					}

					.topic.label.active {
						color: $c-blue-dark;
					}

					.filter-container {
						padding: 0;
						overflow: unset;
					}

					.clear-filter {
						color: $c-slate-dark;
					}

					.filter-header-label,
					.range-slider-legend,
					.item-selector-legend {
						color: $c-slate-dark;
						margin-bottom: 0.5rem;
					}

					.grade-selector {
						.input-label {
							&,
							&:hover,
							&.active:hover {
								.item-label {
									color: inherit;
								}
							}
						}
					}

					input.input-checkbox[type='checkbox'] {
						background-color: $c-white;
						border-color: $c-slate;

						&:checked {
							background-color: $c-white;
							border-color: $c-slate;

							&:after {
								content: '\2713';
							}
						}
					}

					svg.info-icon {
						fill: $c-slate-dark;
						stroke: $c-slate-dark;
					}

					.legacy-link {
						color: $c-blue;
					}
				}

				.apply-filter-button {
					display: none;
				}

				.search-results-content {
					flex: 1;

					.product-promotion .promotion-panel {
						&.aad {
							.aad-video {
								iframe.vimeo-player {
									min-height: 20rem;
								}
							}
						}
					}
				}
			}
		}
	}

	@media screen and (min-width: $bp-width-lg) {
		main.main.find-articles {
			.main-header {
				.main-header-search {
					display: flex;
					@include align-items(center);

					.main-header-search-before {
						@include flex(1);
					}

					.main-header-search-form {
						@include flex(2);
						width: 100%;
						max-width: 42rem;

						.search-box {
							margin-bottom: 0rem;
							width: 100%;
						}
					}

					.main-header-search-after {
						@include flex(1);
					}
				}
			}

			.main-content.wrapper {
				position: relative;

				.search-results .search-results-content {
					padding-right: 3rem;

					.butter-bar {
						margin-right: 5.25rem;
					}

					.product-promotion .promotion-panel {
						&.aad {
							.aad-video {
								iframe.vimeo-player {
									min-height: 26rem;
								}
							}
						}
					}
				}

				.share-buttons.share-glyphs {
					margin: 0;
					position: absolute;
					top: 1.5rem;
					right: 0;
					max-width: 2rem;

					.share-glyph {
						margin-bottom: 0.5rem;
					}
				}
			}
		}
	}
</style>
<template>
	<main
		id="content"
		class="main find-articles">
		<div
			v-if="!gcAddon"
			class="tab-wrapper">
			<div class="wrapper">
				<tab-container
					model-key="contentTab"
					:default-tab="SEARCH"
					class="print-hidden"
					v-if="!studentId">
					<button :data-tab="SEARCH">Search</button>
					<button
						v-if="userContext"
						:data-tab="SCOPE_AND_SEQUENCE">
						Scope & Sequences
					</button>
					<button
						v-if="userContext"
						:data-tab="RMS">
						Reading Mindset Snapshot
					</button>
					<button
						v-if="!userContext && !gcAddon"
						@click="showLoginModal">
						Scope & Sequence
					</button>
					<button
						v-if="!userContext"
						@click="showLoginModal">
						Reading Mindset Snapshot
					</button>
				</tab-container>
			</div>
		</div>
		<student-knowledge-inventory-about-content
			v-if="contentTab === RMS"
			:show-title="true"
			:schedule-today="true"
			ref="contentTabPanel"
			:data-panel="RMS"></student-knowledge-inventory-about-content>
		<scope-and-sequence
			v-if="contentTab === SCOPE_AND_SEQUENCE"
			ref="contentTabPanel"
			:data-panel="SCOPE_AND_SEQUENCE"></scope-and-sequence>
		<form
			v-if="contentTab === SEARCH"
			class="form"
			method="get"
			role="search"
			@submit.prevent="submitSearch"
			ref="contentTabPanel"
			:data-panel="SEARCH">
			<div
				class="main-header-mobile-toggle"
				ref="mobileHeader">
				<div
					class="filter-toggle"
					:class="{
						selected: mobileFilterOpen
					}"
					@click="onMobileFilterClick"
					aria-label="Toggle filters">
					<i
						class="fa-solid fa-lg fa-sliders"
						aria-hidden="true"></i>
				</div>
				<div
					class="search-toggle"
					:class="{
						selected: mobileSearchOpen
					}"
					@click="onMobileSearchClick"
					aria-label="Search">
					<i
						class="icon-search"
						aria-hidden="true"></i>
				</div>
			</div>
			<header class="main-header -has-nav">
				<div class="wrapper">
					<div
						class="main-header-search"
						:class="{
							open: mobileSearchOpen,
							scrolled: filterHeight !== '0px'
						}"
						:style="{ top: filterHeight }">
						<div class="main-header-search-before"></div>
						<div class="main-header-search-form">
							<div class="search-box form-row-input">
								<search-bar-with-suggestions
									:value="q"
									@input.native="onSearchInput($event.target.value)"
									:search-domain="enabledSearchableNodes"
									:on-suggestion-click="onSuggestionClick"
									:category-formatter="suggestionCategoryFormatter"
									placeholder="What are you looking for?"
									:auto-suggest="true"
									maxlength="256">
								</search-bar-with-suggestions>
								<button
									type="submit"
									class="search-action button -large -link icon-search">
									<span class="hidden-visually">Search</span>
								</button>
								<button
									v-if="q && currentSearch.q"
									@click.prevent="resetSearchText"
									class="search-action button -large -link fa-solid fa-circle-xmark">
									<span class="hidden-visually">Clear</span>
								</button>
								<button
									type="button"
									@click="onMobileSearchApply"
									class="search-action button -link mobile-search">
									Search
								</button>
							</div>
						</div>
						<div class="main-header-search-after">
							<div
								class="search-results-header-count"
								v-if="!searchOngoing && isFiltered">
								{{ totalResults | commaGrouping }} Items
							</div>
						</div>
					</div>
				</div>
			</header>
			<div class="main-content wrapper">
				<div class="search-results">
					<aside
						class="search-results-filters"
						:class="{
							open: mobileFilterOpen,
							scrolled: filterHeight !== '0px'
						}"
						:style="{ top: filterHeight }">
						<div
							class="filter-container"
							ref="filters">
							<div class="form-row-input products">
								<div class="filter-header-label">Content Type</div>
								<button
									class="clear-filter button -link"
									v-if="isFiltered"
									type="button"
									@click.prevent="clickResetSearch">
									Clear all filters
								</button>
								<product-selector
									:products="products"
									:gc-addon="gcAddon"
									:on-product-click="toggleProductSelection"
									:on-feature-click="toggleFeatureSelection"
									:on-sub-type-click="toggleSubTypeSelection">
								</product-selector>
								<div
									class="input-select slim margin-left-smedium"
									v-if="currentSearch.pt.includes('TS')">
									<select
										class="book-filter font-small"
										v-model="currentSearch.book"
										aria-label="Book">
										<option
											v-for="book in bookList"
											class="font-normal"
											:key="book.id"
											:value="book.id"
											v-text="book.title"></option>
									</select>
								</div>
							</div>
							<div class="form-row-input audio">
								<div class="filter-header-label">Audio</div>
								<product-selector
									:products="audio"
									:on-product-click="toggleFeatureSelection">
								</product-selector>
							</div>
							<div class="form-row-input activity-types">
								<div class="filter-header-label">Activity Type</div>
								<template v-for="activity in activityTypes">
									<input-item-with-label
										v-if="!activity.children"
										class="activity-type"
										:item="activity"
										:on-item-click="onActivityClick">
									</input-item-with-label>
									<template v-if="activity.children">
										<product-selection-hidden-radio-group
											:product="activity"
											:on-product-click="onActivityClick">
										</product-selection-hidden-radio-group>
									</template>
								</template>
							</div>
							<div class="form-row-input grades">
								<grade-selector
									:grades="grades"
									legend="Grade"
									:on-grade-click="toggleGrade"
									grid>
								</grade-selector>
							</div>
							<div class="form-row-input topics">
								<div class="filter-header-label">Topic</div>
								<topic-selector
									:topic="currentSearch.s && currentSearch.s[0]"
									:on-topic-click="onTopicClick"
									:parent-map="topicsMap">
								</topic-selector>
							</div>
							<div class="form-row-input topics">
								<div class="filter-header-label">Special Collection</div>
								<topic-selector
									:topic="currentSearch.s0 && currentSearch.s0[currentSearch.s0.length - 1]"
									:on-topic-click="onCollectionClick"
									:parent-map="collectionsMap">
								</topic-selector>
							</div>
							<div class="form-row-input text-types">
								<div class="filter-header-label">Text Type</div>
								<input-item-with-label
									v-for="tt in textTypes"
									class="text-type-radio"
									name="textType"
									:key="tt.id"
									:item="tt"
									:on-item-click="toggleTextTypeSelection">
								</input-item-with-label>
							</div>
							<range-slider
								class="word-count-selector form-row-input"
								:values="currentSearch.wc || [0, 3000]"
								:range-min="0"
								:range-max="3000"
								:step="50"
								label="Word Count"
								overflow-unit="+"
								:disabled="wordCountsDisabled"
								@range-change="range => onRangeChange(range, 'wc')"></range-slider>
							<div class="form-row-input lexiles">
								<range-slider
									class="lexile-selector"
									:values="currentSearch.l || [0, 2200]"
									:range-min="0"
									:range-max="2200"
									:step="50"
									label="Lexile<sup>®</sup>"
									unit="L"
									@range-change="range => onRangeChange(range, 'l')"></range-slider>
								<div class="lexile-choices">
									<input-item-with-label
										v-for="lexile in lexileChoices"
										class="text-type-radio"
										:key="lexile.id"
										:item="lexile"
										:on-item-click="toggleLexileChoice">
									</input-item-with-label>
								</div>
							</div>
							<a
								v-if="!gcAddon"
								class="legacy-link"
								href="/lessons-and-units"
								>Go to old site</a
							>
						</div>
						<div
							class="apply-filter-button"
							@click="onMobileFilterApply">
							Apply
						</div>
					</aside>
					<section
						ref="results"
						class="search-results-content"
						:style="{ minHeight: resultsMinHeight }">
						<div
							v-if="didYouMeanNode && !searchOngoing"
							class="did-you-mean">
							Did you mean to search for
							<button
								@click="onDidYouMeanClick"
								type="button"
								class="did-you-mean-keyword button -link">
								"{{ didYouMeanNode.keyword }}"</button
							>?
						</div>
						<div
							class="search-results-header search-pills"
							v-show="undoPills && undoPills.length">
							<undo-pills :pills="undoPills"> </undo-pills>
						</div>
						<butter-bar-promo
							v-if="activeButterBar === 'aad'"
							class="aad-butter-bar">
							<h3>
								ReadWorks Article-A-Day<sup><small>TM</small></sup
								>, now with <a href="/teacher-guide/decodable-texts.html">decodable texts</a>
							</h3>
							<p class="margin-top-bottom-small">
								ReadWorks decodables are all nonfiction and aligned with Article-A-Day sets so that students can build background knowledge while practicing
								their decoding.
							</p>
							<p class="margin-none">
								Article-A-Day is a 10-minute daily routine that dramatically increases students' background knowledge, vocabulary, and stamina.
								<a href="/teacher-guide/article-a-day-routine.html">See how...</a>
							</p>
						</butter-bar-promo>
						<butter-bar-promo
							v-if="activeButterBar === 'express'"
							class="express-butter-bar">
							An express question set has 5 multiple choice questions that scaffold important information toward understanding the main idea of the text.
							Each set also provides practice in key reading strategies, like activating prior knowledge about text structure, inferring and clarifying, and
							questioning!
							<outbound-link
								href="https://about.readworks.org/uploads/7/9/6/7/79679564/annotated_multiple_choice_question_sets.pdf"
								data-outbound-link="EXPRESS_QUESTION_SETS_INFO"
								>Learn more here…</outbound-link
							>
						</butter-bar-promo>
						<header
							class="search-results-header"
							v-if="specialCollection">
							<div class="flex-preventer">
								<figure
									class="article-special-collection-image"
									v-if="specialCollection && specialCollection.imageUrlL">
									<img
										:src="specialCollection.imageUrlL"
										:width="(specialCollection.imageUrlLWidth || 0) / 2"
										alt="Special Collection Logo Image" />
								</figure>
								<div class="article-content">
									<header
										class="article-header"
										v-html="specialCollection.description"></header>
								</div>
							</div>
						</header>
						<div
							v-if="!isFiltered && !searchOngoing"
							class="product-promotion">
							<recommended-content-panel
								v-if="!gcAddon && (classes.length > 1 || currentRecommendedContent.articles.length > 0)"
								:recommendations="currentRecommendedContent.sortedRecommendations"
								:articles="currentRecommendedContent.articles"
								:class-code="recommendationsClassCode"
								:classes="classes"
								:max="recommendationsMax"
								:loading="recommendationsLoading"
								@class-selection="getRecommendationsForClass"
								@recommendation-click="setSearchParam"></recommended-content-panel>
							<featured-topics
								:apply-filters="applyFilters"
								:featured-topics="featuredTopics"></featured-topics>
							<scope-and-sequence-promotion-panel
								v-if="!gcAddon"
								:change-search-view="changeSearchView"></scope-and-sequence-promotion-panel>
							<promotional-collection-panel
								v-if="spotlightPanel"
								:node="spotlightPanel"
								:products="spotlightProducts"
								:on-product-click="onFeaturedContentClick"
								:href="'/promotional-collection#!promoId:' + spotlightPanel.id"
								:debug="debug">
							</promotional-collection-panel>
							<h2>Explore by Content Type</h2>
							<div
								v-for="panel in promotionalPanels"
								:key="panel.componentName"
								:class="['product-promotion-panel-container', `${panel.id}-panel`]">
								<component
									:is="panel.componentName"
									:apply-filters="applyFilters"
									:change-search-view="changeSearchView">
								</component>
								<div
									class="product-promotion-carousel"
									v-if="panel.content">
									<div class="carousel-border-container">
										<h3 :id="panel.id + '-carousel-label'">Featured</h3>
										<featured-content-carousel
											:products="panel.content"
											:on-product-link-click="onFeaturedContentClick"
											:loc="panel.id + '_CAROUSEL'"
											:label-id="panel.id + '-carousel-label'">
										</featured-content-carousel>
									</div>
								</div>
							</div>
						</div>
						<div
							class="articles empty-results"
							:class="emptyResultsLookupKey"
							v-if="totalResults === 0 && !searchOngoing && !searchAborted && !searchError"
							v-html="emptyResultsMessage"></div>
						<div
							class="articles"
							v-if="searchOngoing">
							<h2>
								<img
									:src="loaderImgSrc"
									v-show="searchOngoing"
									alt="Loading"
									role="presentation" />
								Searching...
							</h2>
							<button
								class="button -small"
								@click="abortSearch">
								Cancel
							</button>
						</div>
						<div
							class="articles"
							v-if="searchError">
							<h2 v-if="searchError !== SEARCH_UNAVAILABLE">Error occurred</h2>
							<h2 v-if="searchError === SEARCH_UNAVAILABLE">
								Search is temporarily unavailable.<br />
								Please try again later.
							</h2>
						</div>
						<div
							class="articles"
							v-if="searchAborted">
							<h2>Searching canceled, try another search.</h2>
						</div>
						<div
							class="articles"
							v-if="isFiltered && !searchOngoing && totalResults > 0">
							<article-search-result
								v-for="article in searchResults.hits"
								:key="article.id"
								:article="article"
								:logged-in="!!userContext"
								:my-list="myList"
								:toggle-on-list="toggleOnList"
								:on-product-link-click="setSearchParam"
								:debug="debug">
							</article-search-result>
							<template v-if="softSearchResults.hits.length > 0">
								<div class="soft-search-message">
									<magnifying-glass-illustration class="soft-search-icon"></magnifying-glass-illustration>
									<div class="soft-search-text">
										<div class="soft-search-heading">
											{{ searchResults.hits.length === 0 ? "We couldn't find any " : 'Those are all of the ' }}exact matches for "{{ currentSearch.q }}"
										</div>
										<div class="soft-search-body">We also looked further, and these suggestions might be relevant.</div>
									</div>
								</div>
								<article-search-result
									v-for="article in softSearchResults.hits"
									:key="article.id"
									:article="article"
									:logged-in="!!userContext"
									:my-list="myList"
									:toggle-on-list="toggleOnList"
									:on-product-link-click="setSearchParam"
									:debug="debug">
								</article-search-result>
							</template>
							<pagination
								v-if="isFiltered"
								:page="currentSearch.f"
								:total="totalResults"
								:on-page-change="goToPage">
							</pagination>
						</div>
						<div class="share-button-container">
							<share-buttons
								v-if="!!userContext"
								glyphs
								no-querystring
								:share-thumbnail="pinterestThumbnail"
								:share-title="title"
								:event-data="{ tag: 'find_content' }">
							</share-buttons>
						</div>
						<div class="test-result-checker">
							<span>{{ isFiltered ? 'isfiltered' : 'unfiltered' }}</span>
						</div>
					</section>
				</div>
			</div>
			<!-- end .main-content-wrapper -->
		</form>
		<div
			id="pair-text-skillstrategy-modal"
			class="modal"
			data-modal-window>
			<div class="modal-container">
				<div class="modal-window">
					<div class="modal-window-wrap">
						<button
							class="modal-close"
							data-modal-close>
							<span aria-hidden="true">&times;</span><span class="hidden-visually"> Close</span>
						</button>
						<section class="modal-content">
							<div class="modal-content-main">For Skill/Strategy content, please search Articles.</div>
						</section>
					</div>
				</div>
			</div>
		</div>
	</main>
</template>
